import React, { useState, useRef, useEffect } from 'react';
import { useFloating, arrow, shift, offset, flip } from '@floating-ui/react';

import PropTypes from 'prop-types';

import { Arrow, FloatPosition, PositionContext } from './floating.styles';

function FloatBase({
  trigger,
  triggerStyle,
  position,
  children,
  onClickOutside,
  customOffset,
}) {
  const arrowRef = useRef(null);
  const toolTipRef = useRef(null);

  const [display, setVisible] = useState(false);

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    placement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    middleware: [
      offset(customOffset),
      arrow({ element: arrowRef }),
      flip(),
      shift(),
    ],
    placement: position,
  });

  // Applicable only when triggerStyle === 'always'. Handling for clicks outside of tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolTipRef.current && !toolTipRef.current.contains(event.target)) {
        setVisible(false);
        onClickOutside();
      }
    };

    if (triggerStyle === 'always') {
      setVisible(true);
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClickOutside, triggerStyle]);

  const placementFirst = placement.split('-')[0];

  let staticSide;
  switch (placementFirst) {
    case 'top':
      staticSide = 'bottom';
      break;
    case 'right':
      staticSide = 'left';
      break;
    case 'bottom':
      staticSide = 'top';
      break;
    case 'left':
      staticSide = 'right';
      break;
    default:
      staticSide = 'top';
      break;
  }

  const triggerWrapper = () => {
    if (triggerStyle === 'hover') {
      return (
        <div
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
        >
          {trigger}
        </div>
      );
    }

    /* eslint-disable react/no-unknown-property */
    if (triggerStyle === 'click') {
      return (
        <div
          onClick={() => setVisible(true)}
          onRequestClose={() => setVisible(false)}
        >
          {trigger}
        </div>
      );
    }
    return trigger;
  };
  /* eslint-enable react/no-unknown-property */

  return (
    <>
      <PositionContext ref={reference}>{triggerWrapper()}</PositionContext>
      {display ? (
        <FloatPosition
          position={position}
          ref={floating}
          style={{
            position: strategy,
            top: y ?? '',
            left: x ?? '',
          }}
        >
          <div ref={toolTipRef} className="dark-environment">
            {children}
            <Arrow
              ref={arrowRef}
              style={{
                left: arrowX != null ? `${arrowX - 8}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: '-4px',
              }}
            />
          </div>
        </FloatPosition>
      ) : null}
    </>
  );
}

FloatBase.propTypes = {
  position: PropTypes.string,
  children: PropTypes.any,
  trigger: PropTypes.node,
  triggerStyle: PropTypes.node,
  onClickOutside: PropTypes.func,
  customOffset: PropTypes.number,
};

FloatBase.defaultProps = {
  triggerStyle: 'hover',
  onClickOutside: () => {},
  customOffset: 16,
};

export default FloatBase;
